import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Lightbulb, Clock, Shield, ArrowLeft, Briefcase, Repeat, Network } from 'lucide-react';
import PurchaseWaysSection from './PurchaseWaysSection';
import { addToCart, isInCart } from '@/lib/cartStore';
import AddToCartButton from './cart/AddToCartButton';
import { createStripeCheckout, createStripeLtoCheckout } from '@/lib/checkoutApi';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { fireEvent } from '@/lib/analytics';
import { getParam, setParams, removeParam } from '@/lib/urlState';
import RelatedDomains from './RelatedDomains';
import RecentlyViewed from './RecentlyViewed';
import DomainFAQ from './DomainFAQ';
import DomainInsights from './DomainInsights';
import DomainGapSection from './DomainGapSection';
import DomainTrustAndLogisticsSection from './DomainTrustAndLogisticsSection';
import DomainFinalCallSection from './DomainFinalCallSection';
import ZohoBillingButton from './ZohoBillingButton';
import ZohoBookingButton from './ZohoBookingButton';
import {
  getBinCtaConfig,
  trackExperimentEvent,
  trackExposure,
  EXP_BIN_CTA_V1,
} from '@/lib/experiments';
import { getBinCtaLabel, getAllFlags } from '@/lib/flags';
import { Helmet } from 'react-helmet-async';
import { productLD, faqLD, breadcrumbJsonLd } from '@/lib/structuredData';
import { defaultSite } from '@/lib/seo';
import ExperimentDebugPanel from './ExperimentDebugPanel';
import JsonLD from './JsonLD';
import DomainPricingCard from './domain/DomainPricingCard';
import { motion } from 'framer-motion';
import FavButtonWithCount from './FavButtonWithCount';
import ShimmerButton from './lib/magicui/ShimmerButton';
import { useFavorites } from '@/hooks/useFavorites';
import { useReducedMotion } from '@/hooks/useReducedMotion';

function getInitialFavoritesCount(slug: string): number {
  const sum = Array.from(slug).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return (sum % 19) + 1; // yields 1–19 deterministically per slug
}

// Define domain data type
interface DomainApiData {
  domain: string;
  bin_price: number;
  tags: string[];
  bundle: string | null;
  availability_bin: boolean;
  availability_offer: boolean;
  primary_keyword: string | null;
  domain_is_live: boolean;
  tld: string;
  length: number;
  updated_at: string;
}

// Local, optimized parallax utilities (mirrors Home.tsx)
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}
const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}
function onParallaxScroll() {
  requestParallaxTick();
}
function onParallaxResize() {
  requestParallaxTick();
}
function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}
function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}
function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}
function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}
function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }
  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;
  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();
    if (rect.bottom < farTop || rect.top > farBottom) return;
    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;
    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;
    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);
    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }
    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}

function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);

    ensureParallaxIO(rootMargin);
    startParallax();

    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };

    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }

    parallaxItems.add(item);
    parallaxIo?.observe(el);

    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';

    updateParallax();

    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';

      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);

  return elRef;
}

export default function DomainPage() {
  const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // 0) compute lightweight inputs (no hooks)
  const rawName = name || '';
  const hasName = !!rawName;

  // Generate flexible candidate keys for lookup
  const generateCandidates = (raw: string) => {
    const normalized = raw.toLowerCase().trim();
    const candDotToDash = normalized.replace(/\./g, '-');
    const candDashToDot = normalized.replace(/-/g, '.');
    // Also try original casing in case of exact matches
    const original = raw.trim();
    return Array.from(
      new Set([normalized, candDotToDash, candDashToDot, original].filter(Boolean)),
    );
  };

  // Try lookups in order, stop on first hit
  const tryLoadAny = async (candidates: string[]): Promise<DomainApiData | null> => {
    for (const candidate of candidates) {
      try {
        const { data, error } = await supabase
          .from('domains')
          .select(
            'domain, bin_price, tags, bundle, availability_bin, availability_offer, primary_keyword, domain_is_live, tld, length, updated_at',
          )
          .ilike('domain', candidate)
          .maybeSingle();

        if (data && !error) {
          return data;
        }
      } catch (err) {
        // Continue to next candidate
        console.debug(`Failed to fetch domain ${candidate}:`, err);
      }
    }
    return null;
  };

  // 1) run ALL hooks unconditionally (order never changes)
  // Custom domain data hook with flexible lookup
  const [domainData, setDomainData] = useState<DomainApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoggedView = useRef(false);
  const openedFromQuery = useRef(false);

  useEffect(() => {
    const fetchDomainData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Clean domain name (remove protocol and www)
        const cleanDomain = rawName.replace(/^(https?:\/\/)?(www\.)?/, '');
        const candidates = generateCandidates(cleanDomain);
        const domainData = await tryLoadAny(candidates);

        setDomainData(domainData);
      } catch (err) {
        console.error('Error loading domain data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load domain data');
      } finally {
        setLoading(false);
      }
    };

    fetchDomainData();
  }, [rawName]);

  // Removed modal states - now using /cart for all purchases

  // CTA experiment states
  const [ctaLabel, setCtaLabel] = useState('Buy Now');
  const [ctaVariant, setCtaVariant] = useState<string>('control');

  // URL params for attribution
  const [urlParams, setUrlParams] = useState<Record<string, string>>({});

  // Related domains pool for similar domains section
  const [domainsPool, setDomainsPool] = useState<any[]>([]);
  const { favorites, isFavorited: isFavoritedStore, add: addFavorite, remove: removeFavorite } =
    useFavorites();
  const prefersReducedMotion = useReducedMotion();
  const valuationVideoRef = useRef<HTMLVideoElement | null>(null);
  const playbackRate = 0.3; // slower for smoother, calmer motion

  useEffect(() => {
    const video = valuationVideoRef.current;
    if (!video || prefersReducedMotion) return;

    const tryPlay = () => {
      video.playbackRate = playbackRate;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {
          // Ignore autoplay errors; user gesture will be required
        });
      }
    };

    if (video.readyState >= 1) {
      tryPlay();
    } else {
      video.addEventListener('loadedmetadata', tryPlay, { once: true });
    }

    const enforceRate = () => {
      video.playbackRate = playbackRate;
    };
    video.addEventListener('ratechange', enforceRate);
    video.addEventListener('canplay', enforceRate);

    return () => {
      video.removeEventListener('loadedmetadata', tryPlay);
      video.removeEventListener('ratechange', enforceRate);
      video.removeEventListener('canplay', enforceRate);
    };
  }, [prefersReducedMotion, playbackRate]);

  // Analytics: Track domain view on initial render + update recently viewed
  useEffect(() => {
    if (!hasLoggedView.current && domainData && !loading) {
      fireEvent('domain_view', {
        domain: domainData.domain,
        price: domainData.bin_price ?? null,
        category: domainData.bundle ?? null,
      });
      hasLoggedView.current = true;

      // Update recently viewed domains in localStorage
      try {
        const storedDomains = localStorage.getItem('ns_recent_domains');
        let recentDomains: Array<{
          domain: string;
          viewedAt: number;
          bin_price?: number;
          tld?: string;
          bundle?: string;
        }> = [];

        if (storedDomains) {
          const parsed = JSON.parse(storedDomains);
          if (Array.isArray(parsed)) {
            recentDomains = parsed;
          }
        }

        // Remove existing entry for this domain (if any) and add new one at the front
        recentDomains = recentDomains.filter((d) => d.domain !== domainData.domain);
        recentDomains.unshift({
          domain: domainData.domain,
          viewedAt: Date.now(),
          bin_price: domainData.bin_price,
          tld: domainData.tld,
          bundle: domainData.bundle,
        });

        // Keep only the most recent 30 entries
        recentDomains = recentDomains.slice(0, 30);

        localStorage.setItem('ns_recent_domains', JSON.stringify(recentDomains));

        // Dispatch custom event to notify RecentlyViewed component
        window.dispatchEvent(new CustomEvent('recently-viewed-updated'));
      } catch (error) {
        console.debug('Error updating recent domains:', error);
      }
    }
  }, [domainData, loading]);

  // Handle URL params on mount and one-shot deep-link modal open
  useEffect(() => {
    const handleFlow = async () => {
      const allParams = new URLSearchParams(location.search);
      const attribution: Record<string, string> = {};

      // Collect attribution params: src, host, any utm_*
      const attributionParams = [
        'src',
        'host',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
      ];
      for (const param of attributionParams) {
        const value = allParams.get(param);
        if (value) attribution[param] = value;
      }

      setUrlParams(attribution);

      // One-shot deep-link modal open
      const flow = allParams.get('flow');
      if (domainData && flow && !openedFromQuery.current && ['bin', 'offer'].includes(flow)) {
        openedFromQuery.current = true;

        // Immediately remove ONLY flow param while preserving others
        const cleanParams = new URLSearchParams(location.search);
        cleanParams.delete('flow');
        const newSearch = cleanParams.toString() ? `?${cleanParams}` : '';
        navigate({ pathname: location.pathname, search: newSearch }, { replace: true });

        // Open the appropriate modal
        fireEvent('purchase_open', { domain: domainData.domain, flow });

        // Handle direct checkout flows
        switch (flow) {
          case 'offer':
            navigate('/apply/offer');
            break;
          case 'bin':
            // Direct Stripe checkout for single domain
            if (domainData) {
              try {
                const checkoutUrl = await createStripeCheckout({
                  items: [{ domain: domainData.domain }],
                  quotedTotalCents: Math.round(domainData.bin_price * 100),
                });
                window.location.href = checkoutUrl;
              } catch (error) {
                console.error('Direct checkout error:', error);
                toast({
                  title: 'Checkout failed',
                  description: error instanceof Error ? error.message : 'Unable to start checkout. Please try again.',
                  variant: 'destructive',
                });
              }
            }
            break;
        }
      }
    };

    handleFlow();
  }, [domainData, location.search, location.pathname, navigate]);

  // Fetch domains pool for related domains (lightweight fetch)
  useEffect(() => {
    const fetchDomainsPool = async () => {
      try {
        const { data } = await supabase
          .from('domains')
          .select('domain, bin_price, tags, bundle, tld')
          .eq('domain_is_live', true)
          .eq('owned', true)
          .limit(200); // Lightweight fetch for pool

        setDomainsPool(data || []);
      } catch (error) {
        console.debug('Failed to fetch domains pool for related domains:', error);
        setDomainsPool([]);
      }
    };

    fetchDomainsPool();
  }, []);

  // Reset placeholder favorites when domain changes (local-only, no backend yet)
  const currentDomainSlug = domainData?.domain || rawName;
  const baseFavoritesCount = useMemo(
    () => getInitialFavoritesCount(currentDomainSlug),
    [currentDomainSlug],
  );
  const isFavorited = isFavoritedStore(currentDomainSlug);
  const displayFavoritesCount = isFavorited ? baseFavoritesCount + 1 : baseFavoritesCount;

  const handleFavoriteToggle = useCallback(() => {
    if (isFavorited) {
      removeFavorite(currentDomainSlug);
    } else {
      addFavorite(currentDomainSlug);
    }
  }, [isFavorited, removeFavorite, addFavorite, currentDomainSlug]);

  // Analytics and experiment tracking
  useEffect(() => {
    if (domainData) {
      fireEvent('domain_view', {
        domain: domainData.domain,
        price: domainData.bin_price,
        bundle: domainData.bundle,
        tags: domainData.tags,
      });
    }
  }, [domainData]);

  // Set up BIN CTA from flags
  useEffect(() => {
    if (!domainData?.domain) return;

    // Get CTA label from flags
    getBinCtaLabel().then((label) => {
      setCtaLabel(label);
      setCtaVariant(label === 'Get It Now' ? 'variant' : 'control');
    });
  }, [domainData]);

  // Subtle randomized, layered gradient background (computed once per mount)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    return {
      backgroundColor: '#ffffff',
    };
  }, []);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroPriceRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const useCasesGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });
  const useCasesSubtitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 16 });
  const heroSubtitleRef = useParallaxRef({ speed: -0.15, axis: 'y', maxTranslate: 12 });

  // Generic randomized card animations for use cases grid
  const seeded = (i: number, k = 1) => {
    const x = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
    return x - Math.floor(x);
  };
  const randIndex = (i: number, min: number, max: number, k = 1) =>
    min + seeded(i, k) * (max - min);
  const gridContainerVariants = useMemo(
    () => ({
      hidden: {},
      show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
    }),
    [],
  );
  const getGridItemVariants = useCallback((i: number) => {
    const axisPick = seeded(i, 2) > 0.5 ? 'y' : 'x';
    const fromY = axisPick === 'y' ? randIndex(i, 20, 60, 3) : 0;
    const fromX = axisPick === 'x' ? randIndex(i, -30, 30, 4) : 0;
    const delay = randIndex(i, 0, 0.3, 5);
    const duration = randIndex(i, 0.7, 1.2, 6);
    return {
      hidden: { opacity: 0, x: fromX, y: fromY },
      show: {
        opacity: 1,
        x: 0,
        y: 0,
        transition: { duration, delay, ease: 'easeOut' as const },
      },
    };
  }, []);

  // Analytics: Track structured data injection on mount
  useEffect(() => {
    if (domainData && !loading) {
      fireEvent('seo_ld_injected', {
        types: ['Product', 'FAQPage', 'BreadcrumbList'],
        domain: domainData.domain,
      });
    }
  }, [domainData, loading]);

  // Handle navigation to cart or offer page (replaces modal flows)
  const handlePurchaseAction = (actionType: 'offer' | 'lease' | 'cart') => {
    if (!domainData) return;

    fireEvent('purchase_action', {
      domain: domainData.domain,
      action: actionType,
    });

    switch (actionType) {
      case 'offer':
        navigate(`/apply/offer?domain=${encodeURIComponent(domainData.domain)}`);
        break;
      case 'lease':
        navigate(`/domain/${encodeURIComponent(domainData.domain)}/lease`);
        break;
      case 'cart':
        // Add to cart if not already there
        if (!isInCart(domainData.domain)) {
          const added = addToCart({
            domain: domainData.domain,
            tld: domainData.tld || domainData.domain.split('.').pop() || '',
            binPrice: domainData.bin_price || 0,
          });

          if (added) {
            toast({
              title: 'Added to cart',
              description: `${domainData.domain} has been added to your cart.`,
            });
          }
        }
        navigate('/cart');
        break;
    }
  };

  // Helper functions (moved from after hooks)
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Generate use cases based on domain data with persuasive mini-pitches
  const generateUseCases = (domainData: DomainApiData) => {
    const domain = domainData.domain;
    const primaryKeyword = domainData.primary_keyword || domain.split('.')[0];
    const capitalizedKeyword =
      primaryKeyword.charAt(0).toUpperCase() + primaryKeyword.slice(1);

    // Template-able, outcome-focused use cases
    return [
      {
        title: 'Premium Web Studio',
        lead: `Use ${domain} as the flagship brand for a high-end studio that only takes on serious clients.`,
        bullets: [
          'Signal top-tier delivery before prospects view your portfolio.',
          'Anchor premium project and retainer rates to the brand authority.',
          `Use name@${domain} email to reinforce credibility in every outreach.`,
        ],
        icon: 'briefcase' as const,
      },
      {
        title: 'Subscription Design Service',
        lead: `Productize design/marketing into a recurring service built on ${domain}.`,
        bullets: [
          'Sell monthly retainers with clear SLAs and predictable MRR.',
          `Rank and run ads for ${primaryKeyword} terms with a memorable URL.`,
          'Bundle add-ons (funnels, CRO, emails) to increase account value.',
        ],
        icon: 'repeat' as const,
      },
      {
        title: 'AI-Powered Tools Platform',
        lead: `Launch lightweight AI utilities under ${domain} that capture emails and drive upsells.`,
        bullets: [
          `Own the category term “${primaryKeyword}” in the URL and product UI.`,
          'Gate premium outputs behind trials/credits for quick revenue.',
          'Upsell done-for-you builds or consulting after users see value.',
        ],
        icon: 'network' as const,
      },
      {
        title: 'Authority Content Hub',
        lead: `Publish comparison, pricing, and “how-to” guides to own ${capitalizedKeyword} intent and feed your pipeline.`,
        bullets: [
          'Monetize with lead capture, affiliate, or premium resource bundles.',
          'Use topic clusters to rank and reduce paid CAC over time.',
          'Point every CTA to your offers or to book a consult with Attain.',
        ],
        icon: 'lightbulb' as const,
      },
    ];
  };

  const handleViewDomainClick = () => {
    fireEvent('domain_external_click', {
      domain: domainData?.domain,
    });
  };

  // 2) compute render branches AFTER hooks
  let body: React.ReactNode;

  if (!hasName) {
    body = (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Domain not found</h1>
          <Link to="/bundles" className="text-primary hover:underline">
            Back to Bundles
          </Link>
        </div>
      </div>
    );
  } else if (loading) {
    body = (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading domain information...</p>
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Error loading domain</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/bundles" className="text-primary hover:underline">
            Back to Bundles
          </Link>
        </div>
      </div>
    );
  } else if (!domainData) {
    body = (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">{rawName}</h1>
          <p className="text-muted-foreground mb-8">
            This domain is not currently in my system. Please contact me for more information.
          </p>
          <div className="space-x-4">
            <Link to="/contact">
              <Button>Contact Me</Button>
            </Link>
            <Link to="/bundles">
              <Button variant="outline">Browse All Domains</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  } else {
    const useCases = generateUseCases(domainData);

    // Generate urgency message with dynamic domain name
    const urgencyMessage = `Secure this unique digital asset before it's gone. There is only one ${domainData.domain}.`;

    // Calculate lease monthly payment (8% of BIN)
    const calculateLeaseMonthly = (binPrice: number) => {
      return Math.ceil(binPrice * 0.08);
    };

    // Generate FAQ data for structured data (from DomainFAQ defaults)
    const faqData = [
      {
        q: 'How fast will I own this domain?',
        a: "Most transfers complete within hours after payment clears via a trusted payment provider. I guide you step-by-step until it's in your account.",
      },
      {
        q: 'Is payment safe?',
        a: "Yes—funds are handled by a trusted payment provider, the industry standard. Your money is protected until you confirm you've received the domain.",
      },
      {
        q: 'What exactly do I get?',
        a: 'Exclusive ownership of this domain name. Optional add-ons—like quick setup or consulting—are available if you want help launching.',
      },
      {
        q: 'Why buy here instead of a marketplace?',
        a: "Faster transfer, direct support, and transparent fees—plus occasional buyer bonuses we can't offer on third-party platforms.",
      },
      {
        q: 'Can I lease this domain?',
        a: 'Yes! Start using the domain immediately with monthly payments. You can buy anytime at the BIN price.',
      },
      {
        q: 'Is the price negotiable?',
        a: 'The BIN is the fastest path to own it now. If you need terms, reach out—availability can change at any time.',
      },
    ];

    // Current page URL for structured data
    const currentUrl = `${defaultSite.baseUrl}/domain/${domainData.domain}`;

    body = (
      <>
        {/* SEO Structured Data */}
        <JsonLD
          data={productLD({
            name: domainData.domain,
            url: currentUrl,
            binPrice: domainData.bin_price,
            currency: 'USD',
            availability: 'https://schema.org/InStock',
            sku: domainData.domain,
            brandName: 'Attain Domains',
            imageUrl: `${defaultSite.baseUrl}/favicons/favicon-32.png`,
          })}
        />

        <JsonLD data={faqLD(faqData)} />

        <JsonLD
          data={breadcrumbJsonLd([
            { name: 'Domains', url: `${defaultSite.baseUrl}/domains` },
            { name: domainData.domain, url: currentUrl },
          ])}
        />

        <Helmet>
          {/* Canonical URL */}
          <link rel="canonical" href={currentUrl} />
        </Helmet>

        <div className="min-h-screen animate-page-fade" style={backgroundStyle}>
          {/* White Room Authority Block - Hero Section */}
          <section className="bg-white py-12 md:py-18">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              {/* Header with Breadcrumb */}
              <div className="mb-6 stagger-fade-1">
                <Link
                  to="/domains"
                  className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Domains
                </Link>
              </div>

              {/* Centered Heading Block */}
              <div className="text-center mb-8 space-y-4">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
                  Get {domainData.domain} before it’s gone
                </h1>
                <p
                  ref={heroSubtitleRef as React.RefObject<HTMLParagraphElement>}
                  className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto"
                >
                  Secure this one-of-one digital asset and launch your professional authority in a single strategic move.
                </p>
              </div>

              {/* Hero Card */}
              <Card className="apple-card relative overflow-hidden animate-fade-in-up">
              <CardHeader className="text-center pb-6 px-6 pt-6">
                <div className="mb-4">
                  <CardTitle className="text-4xl md:text-5xl font-bold text-foreground break-words mb-4">
                    {domainData.domain}
                  </CardTitle>
                  {domainData.bin_price && (
                    <div className="text-5xl md:text-6xl lg:text-7xl text-foreground font-bold mb-2">
                      {formatPrice(domainData.bin_price)}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 justify-center">
                  {domainData.bundle && (
                    <Badge
                      variant="outline"
                      className="bg-primary/10 text-primary border-primary/20"
                    >
                      {domainData.bundle}
                    </Badge>
                  )}
                  {domainData.tags?.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardHeader>

              <CardContent className="space-y-6 px-6 pb-6">
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center sm:items-start">
                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center flex-1">
                    {domainData.availability_bin && domainData.bin_price && (
                      <ShimmerButton
                        background="rgba(0, 0, 255, 1)"
                        shimmerColor="#ffffff"
                        borderRadius="0.5rem"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isInCart(domainData.domain)) {
                            navigate('/cart');
                            return;
                          }
                          const added = addToCart({
                            domain: domainData.domain,
                            tld: domainData.tld || domainData.domain.split('.').pop() || '',
                            binPrice: domainData.bin_price,
                          });
                          if (added) {
                            fireEvent('add_to_cart', {
                              domain_id: domainData.domain,
                              slug: domainData.domain,
                              price: domainData.bin_price,
                              location: 'detail',
                            });
                            toast({
                              title: 'Added to cart',
                              description: `${domainData.domain} has been added to your cart.`,
                            });
                            navigate('/cart');
                          } else {
                            toast({
                              title: 'Already in cart',
                              description: `${domainData.domain} is already in your cart.`,
                              variant: 'destructive',
                            });
                          }
                        }}
                        className="flex-1 sm:flex-initial min-w-[140px] px-6 py-3 text-base font-medium"
                      >
                        {isInCart(domainData.domain) ? 'View Cart' : 'Secure This Domain'}
                      </ShimmerButton>
                    )}
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handlePurchaseAction('lease')}
                      className="apple-button-secondary-cta flex-1 sm:flex-initial min-w-[140px]"
                    >
                      Lease to Own
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handlePurchaseAction('offer')}
                      className="apple-button-secondary-cta flex-1 sm:flex-initial min-w-[140px]"
                    >
                      Make Offer
                    </Button>
                  </div>
                  {/* Favorite Control - Desktop: right-aligned, Mobile: below CTAs */}
                  <div className="hidden sm:block">
                    <FavButtonWithCount
                      domain={domainData.domain}
                      favoritesCount={displayFavoritesCount}
                      isFavorited={isFavorited}
                      onToggle={handleFavoriteToggle}
                    />
                  </div>
                </div>

                {/* Favorite Control - Mobile only */}
                <div className="flex justify-center sm:hidden">
                  <FavButtonWithCount
                    domain={domainData.domain}
                    favoritesCount={displayFavoritesCount}
                    isFavorited={isFavorited}
                    onToggle={handleFavoriteToggle}
                  />
                </div>

                {/* Zoho Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <ZohoBookingButton
                    variant="default"
                    domain={domainData.domain}
                    trackView={true}
                    className="flex-1"
                  />
                  <ZohoBillingButton
                    variant="outline"
                    source="domain_detail"
                    domain={domainData.domain}
                    className="flex-1"
                  />
                </div>

                {/* Urgency Message */}
                <div className="text-center py-3 px-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-orange-800 font-medium">{urgencyMessage}</p>
                </div>
              </CardContent>
            </Card>
            </div>
          </section>

          {/* Strategic Use Cases - White Room */}
          <section className="bg-white py-14 md:py-18">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6 space-y-10">
              <div className="text-center space-y-3">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  Strategic Use Cases: What You Can Build on {domainData.domain}
                </h2>
                <p
                  ref={useCasesSubtitleRef as React.RefObject<HTMLParagraphElement>}
                  className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto"
                >
                  Concrete ways this domain can power agencies, products, and recurring revenue—starting immediately.
                </p>
              </div>

              <motion.div
                ref={useCasesGridRef as React.RefObject<HTMLDivElement>}
                className="grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3"
                variants={gridContainerVariants}
                initial="hidden"
                animate="show"
              >
                {useCases.map((useCase, index) => {
                  const iconMap = {
                    briefcase: Briefcase,
                    repeat: Repeat,
                    network: Network,
                    lightbulb: Lightbulb,
                  };
                  const Icon = iconMap[useCase.icon] || Lightbulb;

                  return (
                    <motion.div
                      key={index}
                      variants={getGridItemVariants(index)}
                      className="group relative bg-white border border-slate-200/80 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-200 p-6 flex flex-col gap-3 hover:-translate-y-1"
                    >
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#0000FF]/10 via-[#0000FF]/20 to-[#0000FF]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">{useCase.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{useCase.lead}</p>
                      <ul className="space-y-2 text-sm text-foreground/80">
                        {useCase.bullets.map((bullet: string, i: number) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/70" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="pt-2">
                        <Link
                          to="/services"
                          className="text-primary text-sm font-medium inline-flex items-center gap-1 hover:underline"
                        >
                          See how my Launch Studio can help →
                        </Link>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>

              <div className="text-center text-sm text-muted-foreground">
                Want help turning {domainData.domain} into a fully-booked brand?{' '}
                <Link to="/services" className="text-primary hover:underline">
                  Explore my Launch Studio services
                </Link>
                .
              </div>
            </div>
          </section>

          {/* Valuation & Strategic Insights - Black Room */}
          <section className="relative overflow-hidden bg-[#020617] py-16 md:py-20">
            {!prefersReducedMotion && (
              <video
                ref={valuationVideoRef}
                className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                autoPlay
                muted
                defaultMuted
                loop
                playsInline
                preload="auto"
                aria-hidden="true"
              >
                <source
                  src="/media/000000istockphoto-1486497633-640_adpp_is.mp4"
                  type="video/mp4"
                />
              </video>
            )}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-black/80"
            />

            <div className="relative max-w-[1200px] mx-auto px-4 md:px-6 space-y-10">
              <div className="text-center space-y-3">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                  Valuation & Strategic Insights
                </h2>
                <p className="text-base md:text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
                  Market-backed reasons {domainData.domain} delivers brand authority, search leverage, and long-term equity.
                </p>
              </div>
              <DomainInsights
                domain={domainData.domain}
                binPrice={domainData.bin_price}
                tld={domainData.tld}
                tags={domainData.tags}
                variant="dark"
                showHeading={false}
              />
            </div>
          </section>

          {/* Gap Section - White Room */}
          <DomainGapSection domainName={domainData.domain} category={domainData.bundle} />

          {/* Offer Section - White Room */}
          <section className="bg-white py-16 md:py-20">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6 space-y-12">
              <div className="text-center space-y-3">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  Choose How You Want to Secure {domainData.domain}
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                  Buy it outright, lease-to-own over time, or structure an offer that fits your cashflow and growth goals.
                </p>
              </div>

              <DomainPricingCard
                domain={domainData.domain}
                onAction={async (action) => {
                  if (action === 'bin') {
                    handlePurchaseAction('cart');
                  } else if (action === 'lto_12' || action === 'lto_24' || action === 'lto_36') {
                    try {
                      const term = action === 'lto_12' ? '12m' : action === 'lto_24' ? '24m' : '36m';
                      const checkoutUrl = await createStripeLtoCheckout(domainData.domain, term);
                      window.location.href = checkoutUrl;
                    } catch (error) {
                      console.error('LTO checkout error:', error);
                      toast({
                        title: 'Checkout failed',
                        description:
                          error instanceof Error ? error.message : 'Unable to start lease checkout. Please try again.',
                        variant: 'destructive',
                      });
                    }
                  } else if (action === 'contact') {
                    navigate('/contact');
                  }
                }}
              />

              {/* Secure Transfers */}
              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold text-foreground">100% Secure Transfers</h3>
                <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
                  All purchase options include secure payments, guided domain transfer, and dedicated support until you control DNS at your chosen registrar.
                </p>
              </div>

              <div id="ways-to-get-domain">
                <PurchaseWaysSection
                  variant="domain"
                  domainPrice={domainData.bin_price}
                  domain={domainData.domain}
                  tld={domainData.tld || domainData.domain.split('.').pop() || ''}
                  onPurchaseAction={handlePurchaseAction}
                />
              </div>

            </div>
          </section>

          {/* Trust & Logistics */}
          <DomainTrustAndLogisticsSection domainName={domainData.domain} />

          {/* Related + Recently Viewed with gradient transition to blue */}
          <section className="bg-gradient-to-b from-white to-[#0000FF] py-16">
            <div className="max-w-4xl mx-auto px-4 space-y-16">
              {/* Related Domains */}
              <div className="stagger-fade-4">
                <RelatedDomains
                  current={{
                    domain: domainData.domain,
                    tags: domainData.tags,
                    bundle: domainData.bundle,
                    tld: domainData.tld,
                  }}
                  pool={domainsPool}
                  className="text-white"
                />
              </div>

              {/* Recently Viewed */}
              <div className="stagger-fade-4">
                <RecentlyViewed currentDomain={domainData.domain} />
              </div>
            </div>
          </section>

          {/* Final Call - Blue Room (bottom of page, above footer) */}
          <DomainFinalCallSection
            domainName={domainData.domain}
            favoritesCount={displayFavoritesCount}
            onPrimary={() => handlePurchaseAction('cart')}
            onSecondary={() => navigate('/contact')}
          />
        </div>
      </>
    );
  }

  // 3) return once, with head/Helmet and body in a stable wrapper
  return (
    <>
      {body}

      {/* Modals removed - now using /cart and full-page flows */}
    </>
  );
}
